import { forwardRef, type ReactNode } from "react";

import { Tooltip, type TooltipPlacement } from "../Tooltip";
import { IconButton, type IconButtonProps } from "./IconButton";
import { getNonEmptyString, isRenderableNode } from "../../utils/reactNode";

type TooltipConfig = {
  content: ReactNode;
  placement?: TooltipPlacement;
};

export interface TooltipIconButtonProps extends IconButtonProps {
  children: ReactNode;
  tooltip?: ReactNode | TooltipConfig;
  disabledTooltip?: ReactNode;
}

const isTooltipConfig = (tooltip: ReactNode | TooltipConfig): tooltip is TooltipConfig => {
  return (
    typeof tooltip === "object" &&
    tooltip !== null &&
    !Array.isArray(tooltip) &&
    "content" in tooltip
  );
};

/** IconButton composed with c6 Tooltip. */
export const TooltipIconButton = forwardRef<HTMLButtonElement, TooltipIconButtonProps>(
  (
    {
      tooltip,
      disabledTooltip,
      disabled = false,
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledBy,
      ...rest
    },
    ref,
  ) => {
    const tooltipContent =
      tooltip == null ? undefined : isTooltipConfig(tooltip) ? tooltip.content : tooltip;
    const placement =
      tooltip != null && isTooltipConfig(tooltip) ? (tooltip.placement ?? "bottom") : "bottom";
    const visibleTooltip =
      disabled && isRenderableNode(disabledTooltip) ? disabledTooltip : tooltipContent;
    const fallbackLabel = getNonEmptyString(tooltipContent);
    const usesTooltipAsLabel =
      fallbackLabel !== undefined && ariaLabel == null && ariaLabelledBy == null;
    const describeTrigger = !(usesTooltipAsLabel && visibleTooltip === tooltipContent);

    const button = (
      <IconButton
        ref={ref}
        disabled={disabled}
        aria-label={ariaLabel ?? (ariaLabelledBy == null ? fallbackLabel : undefined)}
        aria-labelledby={ariaLabelledBy}
        {...rest}
      />
    );

    if (!isRenderableNode(visibleTooltip)) {
      return button;
    }

    return (
      <Tooltip content={visibleTooltip} placement={placement} describeTrigger={describeTrigger}>
        {button}
      </Tooltip>
    );
  },
);

TooltipIconButton.displayName = "TooltipIconButton";
